/**
 * Plain-language reading of an RRULE (§7.2).
 *
 * Deliberately narrow: it covers the subset the schedule editor can produce, and
 * returns the raw rule when it meets anything else. A confidently wrong sentence
 * about when a bot will post is worse than showing the rule itself — the server's
 * `rrule_plain` is authoritative for saved schedules; this is the live preview
 * while the owner is still typing.
 */

const DAY_NAME: Record<string, string> = {
  MO: 'Monday',
  TU: 'Tuesday',
  WE: 'Wednesday',
  TH: 'Thursday',
  FR: 'Friday',
  SA: 'Saturday',
  SU: 'Sunday',
};

function list(parts: string[]): string {
  if (parts.length <= 1) return parts[0] ?? '';
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

export function describeRRule(rrule: string, timezone: string): string {
  const body = rrule.replace(/^RRULE:/i, '');
  const parts = new Map<string, string>();
  for (const kv of body.split(';')) {
    const [k, v] = kv.split('=');
    if (k && v) parts.set(k.toUpperCase(), v);
  }

  const freq = parts.get('FREQ');
  if (!freq) return rrule;

  const interval = Number(parts.get('INTERVAL') ?? '1');
  const byDay = parts.get('BYDAY')?.split(',') ?? [];
  const hour = parts.get('BYHOUR');
  const minute = parts.get('BYMINUTE') ?? '00';
  const count = parts.get('COUNT');

  let when: string;
  switch (freq) {
    case 'DAILY':
      when = interval === 1 ? 'Every day' : `Every ${interval} days`;
      break;
    case 'WEEKLY': {
      const days = list(byDay.map((d) => DAY_NAME[d] ?? d));
      if (days) {
        when = interval === 1 ? `Every ${days}` : `Every ${interval} weeks on ${days}`;
      } else {
        when = interval === 1 ? 'Every week' : `Every ${interval} weeks`;
      }
      break;
    }
    case 'MONTHLY':
      when = interval === 1 ? 'Every month' : `Every ${interval} months`;
      break;
    case 'HOURLY':
      return `Every ${interval === 1 ? 'hour' : `${interval} hours`}${count ? `, ${count} times` : ''}.`;
    default:
      return rrule;
  }

  const at = hour ? ` at ${hour.padStart(2, '0')}:${minute.padStart(2, '0')}` : '';
  const limit = count ? `, ${count} times overall` : '';
  return `${when}${at} (${timezone})${limit}.`;
}
